import { z } from 'zod'

import { ActionPlan } from './plan.ts'
import { WebTestScenario, WebTestTargetKind } from './scenario.ts'

const id = z.string().min(1).max(200)
const isoDate = z.string().datetime()

export const RunOrigin = z.enum(['mcp', 'cli', 'dashboard', 'github-pr', 'manual-remote'])
export type RunOrigin = z.infer<typeof RunOrigin>

export const RunTarget = z.enum(['local-web-lab', 'pr-web-lab', 'local-electron'])
export type RunTarget = z.infer<typeof RunTarget>

export const RunState = z.enum([
  'queued',
  'preparing',
  'exploring',
  'compiling',
  'stabilizing',
  'running',
  'diagnosing',
  'cancelling',
  'terminal'
])
export type RunState = z.infer<typeof RunState>

export const AttemptPurpose = z.enum([
  'discovery',
  'stabilization',
  'deterministic',
  'clean-retry',
  'diagnosis',
  'repair-discovery',
  'repair-validation'
])
export type AttemptPurpose = z.infer<typeof AttemptPurpose>

export const RunStatus = z.enum([
  'passed',
  'failed',
  'cancelled',
  'timed-out',
  'infrastructure-error',
  'needs-review'
])
export type RunStatus = z.infer<typeof RunStatus>

export const FailureClassification = z.enum([
  'product-failure',
  'test-drift',
  'scenario-drift',
  'flaky',
  'infrastructure-error',
  'needs-review',
  'unknown'
])
export type FailureClassification = z.infer<typeof FailureClassification>

export const StepStatus = z.enum(['pending', 'running', 'passed', 'failed', 'skipped', 'cancelled'])
export type StepStatus = z.infer<typeof StepStatus>

export const ArtifactKind = z.enum([
  'screenshot-before',
  'screenshot-after',
  'screenshot-failure',
  'screenshot-baseline',
  'screenshot-diff',
  'trace',
  'recording',
  'accessibility-snapshot',
  'dom-excerpt',
  'console-log',
  'network-log',
  'scenario-snapshot',
  'plan-snapshot',
  'candidate-plan',
  'plan-diff',
  'raw-events',
  'result-json',
  'junit',
  'html-report'
])
export type ArtifactKind = z.infer<typeof ArtifactKind>

export const ArtifactRef = z
  .object({
    id,
    kind: ArtifactKind,
    name: z.string().min(1).max(500),
    contentType: z.string().min(1).max(200),
    byteSize: z.number().int().nonnegative(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    redacted: z.boolean(),
    createdAt: isoDate,
    expiresAt: isoDate.optional(),
    downloadUrl: z.string().url().optional()
  })
  .strict()
export type ArtifactRef = z.infer<typeof ArtifactRef>

/**
 * Server-supplied GitHub facts captured when a PR verifier run is admitted.
 * They travel with the immutable run record, rather than asking the dashboard
 * browser to call GitHub (or exposing a GitHub token to it).
 */
export const PullRequestMetadata = z
  .object({
    title: z.string().trim().min(1).max(500),
    description: z.string().max(65_536).optional(),
    observedAt: isoDate,
    status: z.enum(['draft', 'ready', 'merged', 'closed']),
    author: z
      .object({
        login: z.string().trim().min(1).max(100),
        avatarUrl: z.string().url().max(2_048)
      })
      .strict()
      .optional(),
    checks: z
      .object({
        passed: z.number().int().nonnegative(),
        total: z.number().int().nonnegative()
      })
      .strict()
      .refine((checks) => checks.passed <= checks.total, {
        message: 'Passed checks cannot exceed total checks'
      }),
    reviewThreads: z
      .object({
        unresolved: z.number().int().nonnegative(),
        total: z.number().int().nonnegative()
      })
      .strict()
      .refine((threads) => threads.unresolved <= threads.total, {
        message: 'Unresolved review threads cannot exceed total threads'
      })
  })
  .strict()
export type PullRequestMetadata = z.infer<typeof PullRequestMetadata>

export const RunSource = z
  .object({
    repository: z.string().min(1).max(300).optional(),
    branch: z.string().min(1).max(500).optional(),
    sha: z
      .string()
      .regex(/^[a-f0-9]{7,64}$/)
      .optional(),
    pullRequest: z.number().int().positive().optional(),
    scenarioPath: z
      .string()
      .min(1)
      .max(1_000)
      .regex(/^(?!\/)(?!.*(?:^|\/)\.\.(?:\/|$))[^\0]+$/)
      .optional(),
    planPath: z
      .string()
      .min(1)
      .max(1_000)
      .regex(/^(?!\/)(?!.*(?:^|\/)\.\.(?:\/|$))[^\0]+$/)
      .optional(),
    pullRequestMetadata: PullRequestMetadata.optional()
  })
  .strict()
  .superRefine((source, context) => {
    if (source.pullRequestMetadata && source.pullRequest === undefined) {
      context.addIssue({
        code: 'custom',
        message: 'Pull request metadata requires a pull request number',
        path: ['pullRequestMetadata']
      })
    }
  })
export type RunSource = z.infer<typeof RunSource>

export const RunStepResult = z
  .object({
    id,
    scenarioStepId: id,
    description: z.string().min(1).max(500),
    phase: z.enum(['setup', 'test', 'cleanup']).optional(),
    status: StepStatus,
    startedAt: isoDate.optional(),
    finishedAt: isoDate.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    actionSummary: z.string().max(2_000).optional(),
    locatorSummary: z.string().max(2_000).optional(),
    expected: z.string().max(10_000).optional(),
    actual: z.string().max(10_000).optional(),
    error: z.string().max(20_000).optional(),
    artifactIds: z.array(id).default([])
  })
  .strict()
export type RunStepResult = z.infer<typeof RunStepResult>

export const RunAttempt = z
  .object({
    id,
    number: z.number().int().positive(),
    purpose: AttemptPurpose,
    status: RunStatus.optional(),
    startedAt: isoDate,
    finishedAt: isoDate.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    steps: z.array(RunStepResult).default([]),
    artifactIds: z.array(id).default([]),
    error: z.string().max(20_000).optional()
  })
  .strict()
export type RunAttempt = z.infer<typeof RunAttempt>

export const ModelUsage = z
  .object({
    model: z.string().min(1).max(200),
    gatewayRequestId: z.string().min(1).max(500).optional(),
    inputTokens: z.number().int().nonnegative().optional(),
    outputTokens: z.number().int().nonnegative().optional(),
    estimatedCostUsd: z.number().nonnegative().optional(),
    latencyMs: z.number().int().nonnegative()
  })
  .strict()
export type ModelUsage = z.infer<typeof ModelUsage>

export const RunDiagnosis = z
  .object({
    classification: FailureClassification,
    confidence: z.number().min(0).max(1),
    summary: z.string().min(1).max(4_000),
    evidenceArtifactIds: z.array(id).default([]),
    evidenceStepIds: z.array(id).default([]),
    nextAction: z.string().min(1).max(2_000)
  })
  .strict()
export type RunDiagnosis = z.infer<typeof RunDiagnosis>

export const RunExecution = z
  .object({
    mode: z.enum(['local-web-lab', 'electron-managed', 'electron-attach', 'remote-browser-run']),
    stateful: z.boolean(),
    reproducible: z.boolean(),
    targetVersion: z.string().min(1).max(200).optional(),
    debugEndpoint: z.string().url().max(2_000).optional(),
    userDataMode: z
      .enum(['isolated', 'developer-owned', 'service-owned', 'not-applicable'])
      .optional(),
    webLabRuntime: z.enum(['local', 'vps', 'fixture']).optional(),
    webLabSessionIds: z.array(id).max(100).optional(),
    webLabProcess: z
      .object({
        ownership: z.enum(['developer-owned', 'run-owned']),
        command: z.array(z.string().min(1).max(2_000)).max(100).optional(),
        environment: z.record(z.string().min(1).max(200), z.string().max(4_000)).optional()
      })
      .strict()
      .superRefine((process, context) => {
        if (
          process.ownership === 'developer-owned' &&
          (process.command !== undefined || process.environment !== undefined)
        ) {
          context.addIssue({
            code: 'custom',
            message:
              'Developer-owned Web Lab processes cannot claim a managed command or environment'
          })
        }
        if (
          process.ownership === 'run-owned' &&
          (process.command === undefined || process.environment === undefined)
        ) {
          context.addIssue({
            code: 'custom',
            message: 'Run-owned Web Lab processes require an explicit command and environment'
          })
        }
      })
      .optional(),
    seed: z
      .object({
        status: z.enum(['none', 'unsupported', 'applied']),
        fixture: z.string().min(1).max(200).optional()
      })
      .strict()
      .superRefine((seed, context) => {
        if (seed.status === 'none' && seed.fixture !== undefined) {
          context.addIssue({
            code: 'custom',
            message: 'A run without a seed cannot name a fixture',
            path: ['fixture']
          })
        }
        if (seed.status !== 'none' && seed.fixture === undefined) {
          context.addIssue({
            code: 'custom',
            message: `${seed.status} seed status requires the fixture name`,
            path: ['fixture']
          })
        }
      })
      .optional(),
    warning: z.string().min(1).max(1_000).optional()
  })
  .strict()
export type RunExecution = z.infer<typeof RunExecution>

export const WebTestRun = z
  .object({
    schemaVersion: z.literal(1),
    id,
    suiteRunId: id.optional(),
    scenarioId: id,
    scenarioHash: z.string().regex(/^[a-f0-9]{64}$/),
    planHash: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
    origin: RunOrigin,
    target: RunTarget,
    targetKind: WebTestTargetKind,
    state: RunState,
    status: RunStatus.optional(),
    classification: FailureClassification.optional(),
    source: RunSource.default({}),
    initiatedBy: z.string().min(1).max(300),
    createdAt: isoDate,
    startedAt: isoDate.optional(),
    finishedAt: isoDate.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    currentStageSummary: z.string().max(1_000).optional(),
    attempts: z.array(RunAttempt).default([]),
    artifacts: z.array(ArtifactRef).default([]),
    diagnosis: RunDiagnosis.optional(),
    modelUsage: z.array(ModelUsage).default([]),
    execution: RunExecution.optional(),
    reproductionCommand: z.string().min(1).max(4_000).optional(),
    dashboardUrl: z.string().url().optional()
  })
  .strict()
  .superRefine((run, context) => {
    if (run.state === 'terminal' && run.status === undefined) {
      context.addIssue({
        code: 'custom',
        message: 'Terminal runs require a status',
        path: ['status']
      })
    }
    if (run.state !== 'terminal' && run.status !== undefined) {
      context.addIssue({
        code: 'custom',
        message: 'Only terminal runs may have a terminal status',
        path: ['status']
      })
    }
  })
export type WebTestRun = z.infer<typeof WebTestRun>

export const RunEvent = z
  .object({
    schemaVersion: z.literal(1),
    id,
    runId: id,
    sequence: z.number().int().nonnegative(),
    occurredAt: isoDate,
    type: z.enum([
      'run-state',
      'attempt-started',
      'attempt-finished',
      'step-started',
      'step-finished',
      'artifact-created',
      'diagnosis-created',
      'log',
      'heartbeat'
    ]),
    summary: z.string().min(1).max(2_000),
    attemptId: id.optional(),
    stepId: id.optional(),
    artifactId: id.optional(),
    state: RunState.optional(),
    status: RunStatus.optional(),
    classification: FailureClassification.optional()
  })
  .strict()
  .superRefine((event, context) => {
    if (event.id !== `${event.runId}:${event.sequence}`) {
      context.addIssue({
        code: 'custom',
        message: 'Event id must be the canonical <run-id>:<sequence> cursor',
        path: ['id']
      })
    }
    if (event.type === 'run-state' && event.state === undefined) {
      context.addIssue({
        code: 'custom',
        message: 'Run-state events require the resulting state',
        path: ['state']
      })
    }
    if (
      (event.type === 'attempt-started' || event.type === 'attempt-finished') &&
      event.attemptId === undefined
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Attempt events require an attempt id',
        path: ['attemptId']
      })
    }
    if (
      (event.type === 'step-started' || event.type === 'step-finished') &&
      (event.attemptId === undefined || event.stepId === undefined)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Step events require attempt and step ids',
        path: event.attemptId === undefined ? ['attemptId'] : ['stepId']
      })
    }
    if (event.type === 'artifact-created' && event.artifactId === undefined) {
      context.addIssue({
        code: 'custom',
        message: 'Artifact events require an artifact id',
        path: ['artifactId']
      })
    }
  })
export type RunEvent = z.infer<typeof RunEvent>

export const RunEventBatch = z
  .object({
    schemaVersion: z.literal(1),
    runId: id,
    afterSequence: z.number().int().min(-1),
    events: z.array(RunEvent).max(1_000),
    nextAfterSequence: z.number().int().min(-1)
  })
  .strict()
  .superRefine((batch, context) => {
    let previous = batch.afterSequence
    for (const [index, event] of batch.events.entries()) {
      if (event.runId !== batch.runId) {
        context.addIssue({
          code: 'custom',
          message: 'Every event in a batch must belong to the requested run',
          path: ['events', index, 'runId']
        })
      }
      if (event.sequence !== previous + 1) {
        context.addIssue({
          code: 'custom',
          message: `Expected resumable event sequence ${previous + 1}`,
          path: ['events', index, 'sequence']
        })
      }
      previous = event.sequence
    }
    if (batch.nextAfterSequence !== previous) {
      context.addIssue({
        code: 'custom',
        message: 'nextAfterSequence must identify the final returned event',
        path: ['nextAfterSequence']
      })
    }
  })
export type RunEventBatch = z.infer<typeof RunEventBatch>

export const RunStatusProjection = z
  .object({
    schemaVersion: z.literal(1),
    id,
    scenarioId: id,
    state: RunState,
    status: RunStatus.optional(),
    classification: FailureClassification.optional(),
    stage: z.string().max(1_000).optional(),
    durationMs: z.number().int().nonnegative().optional(),
    attemptCount: z.number().int().nonnegative(),
    firstActionableFailure: z
      .object({
        stepId: id,
        description: z.string().min(1).max(500),
        expected: z.string().max(10_000).optional(),
        actual: z.string().max(10_000).optional(),
        error: z.string().max(20_000).optional(),
        artifactIds: z.array(id)
      })
      .strict()
      .optional(),
    reproductionCommand: z.string().min(1).max(4_000).optional(),
    artifactCount: z.number().int().nonnegative()
  })
  .strict()
export type RunStatusProjection = z.infer<typeof RunStatusProjection>

export const RunAggregateProjection = z
  .object({
    schemaVersion: z.literal(1),
    kind: z.enum(['suite', 'pull-request']),
    key: z.string().min(1).max(300),
    total: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    terminal: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    needsReview: z.number().int().nonnegative(),
    infrastructureErrors: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    verdict: z.enum(['active', 'passed', 'attention', 'infrastructure', 'empty']),
    runIds: z.array(id).max(1_000),
    updatedAt: isoDate
  })
  .strict()
  .refine((aggregate) => aggregate.active + aggregate.terminal === aggregate.total, {
    message: 'Aggregate active and terminal counts must equal total'
  })
export type RunAggregateProjection = z.infer<typeof RunAggregateProjection>

export const RunAggregateProjectionV2 = z
  .object({
    schemaVersion: z.literal(2),
    kind: z.enum(['suite', 'pull-request', 'branch', 'scenario', 'status']),
    key: z.string().min(1).max(512),
    label: z.string().min(1).max(300),
    total: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    terminal: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    needsReview: z.number().int().nonnegative(),
    infrastructureErrors: z.number().int().nonnegative(),
    timedOut: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    verdict: z.enum(['active', 'passed', 'attention', 'infrastructure', 'empty']),
    runIds: z.array(id).max(1_000),
    actionRunId: id.optional(),
    latestRunId: id.optional(),
    repository: z.string().min(1).max(300).optional(),
    pullRequest: z.number().int().positive().optional(),
    sourceSha: z
      .string()
      .regex(/^[a-f0-9]{7,64}$/)
      .optional(),
    pullRequestMetadata: PullRequestMetadata.optional(),
    updatedAt: isoDate
  })
  .strict()
  .refine((aggregate) => aggregate.active + aggregate.terminal === aggregate.total, {
    message: 'Aggregate active and terminal counts must equal total'
  })
  .refine(
    (aggregate) =>
      aggregate.active +
        aggregate.passed +
        aggregate.failed +
        aggregate.needsReview +
        aggregate.infrastructureErrors +
        aggregate.timedOut +
        aggregate.cancelled ===
      aggregate.total,
    { message: 'Aggregate status counts must equal total' }
  )
export type RunAggregateProjectionV2 = z.infer<typeof RunAggregateProjectionV2>

/** Exact persisted definition and bounded AI-exploration context for one run. */
export const RunDefinitionProjection = z
  .object({
    schemaVersion: z.literal(1),
    runId: id,
    scenario: WebTestScenario,
    plan: ActionPlan.optional(),
    exploration: z
      .object({
        used: z.boolean(),
        systemInstruction: z.string().min(1).max(2_000).optional(),
        contextSummary: z.string().min(1).max(2_000)
      })
      .strict()
  })
  .strict()
export type RunDefinitionProjection = z.infer<typeof RunDefinitionProjection>

export function projectRunStatus(run: WebTestRun): RunStatusProjection {
  const failedStep = run.attempts
    .flatMap((attempt) => attempt.steps)
    .find((step) => step.status === 'failed')
  return RunStatusProjection.parse({
    schemaVersion: 1,
    id: run.id,
    scenarioId: run.scenarioId,
    state: run.state,
    ...(run.status === undefined ? {} : { status: run.status }),
    ...(run.classification === undefined ? {} : { classification: run.classification }),
    ...(run.currentStageSummary === undefined ? {} : { stage: run.currentStageSummary }),
    ...(run.durationMs === undefined ? {} : { durationMs: run.durationMs }),
    attemptCount: run.attempts.length,
    ...(failedStep === undefined
      ? {}
      : {
          firstActionableFailure: {
            stepId: failedStep.scenarioStepId,
            description: failedStep.description,
            ...(failedStep.expected === undefined ? {} : { expected: failedStep.expected }),
            ...(failedStep.actual === undefined ? {} : { actual: failedStep.actual }),
            ...(failedStep.error === undefined ? {} : { error: failedStep.error }),
            artifactIds: failedStep.artifactIds
          }
        }),
    ...(run.reproductionCommand === undefined
      ? {}
      : { reproductionCommand: run.reproductionCommand }),
    artifactCount: run.artifacts.length
  })
}

export function aggregateRuns(
  kind: RunAggregateProjection['kind'],
  key: string,
  runs: WebTestRun[],
  updatedAt = new Date().toISOString()
): RunAggregateProjection {
  const active = runs.filter((run) => run.state !== 'terminal').length
  const infrastructureErrors = runs.filter((run) => run.status === 'infrastructure-error').length
  const needsReview = runs.filter((run) => run.status === 'needs-review').length
  const failed = runs.filter((run) => run.status === 'failed').length
  const passed = runs.filter((run) => run.status === 'passed').length
  const cancelled = runs.filter((run) => run.status === 'cancelled').length
  const verdict =
    runs.length === 0
      ? 'empty'
      : active > 0
        ? 'active'
        : failed > 0 || needsReview > 0
          ? 'attention'
          : infrastructureErrors > 0
            ? 'infrastructure'
            : 'passed'
  return RunAggregateProjection.parse({
    schemaVersion: 1,
    kind,
    key,
    total: runs.length,
    active,
    terminal: runs.length - active,
    passed,
    failed,
    needsReview,
    infrastructureErrors,
    cancelled,
    verdict,
    runIds: runs.map((run) => run.id),
    updatedAt
  })
}

export function aggregateRunsV2(
  kind: RunAggregateProjectionV2['kind'],
  key: string,
  runs: WebTestRun[],
  updatedAt = new Date().toISOString()
): RunAggregateProjectionV2 {
  const active = runs.filter((run) => run.state !== 'terminal').length
  const infrastructureErrors = runs.filter((run) => run.status === 'infrastructure-error').length
  const needsReview = runs.filter((run) => run.status === 'needs-review').length
  const failed = runs.filter((run) => run.status === 'failed').length
  const passed = runs.filter((run) => run.status === 'passed').length
  const timedOut = runs.filter((run) => run.status === 'timed-out').length
  const cancelled = runs.filter((run) => run.status === 'cancelled').length
  const verdict =
    runs.length === 0
      ? 'empty'
      : active > 0
        ? 'active'
        : failed > 0 || needsReview > 0 || timedOut > 0
          ? 'attention'
          : infrastructureErrors > 0
            ? 'infrastructure'
            : 'passed'
  return RunAggregateProjectionV2.parse({
    schemaVersion: 2,
    kind,
    key,
    label: key,
    total: runs.length,
    active,
    terminal: runs.length - active,
    passed,
    failed,
    needsReview,
    infrastructureErrors,
    timedOut,
    cancelled,
    verdict,
    runIds: runs.map((run) => run.id),
    ...(runs.length === 0
      ? {}
      : {
          actionRunId: [...runs].sort(compareAggregateActionability)[0]!.id,
          latestRunId: [...runs].sort(
            (left, right) =>
              new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
          )[0]!.id
        }),
    updatedAt
  })
}

function compareAggregateActionability(left: WebTestRun, right: WebTestRun): number {
  const rank = (run: WebTestRun): number => {
    if (run.status === 'failed') return 0
    if (run.status === 'needs-review') return 1
    if (run.status === 'timed-out') return 2
    if (run.status === 'infrastructure-error') return 3
    if (run.status === 'cancelled') return 4
    if (run.state !== 'terminal') return 5
    return 6
  }
  return (
    rank(left) - rank(right) ||
    new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )
}

const transitions: Record<RunState, ReadonlySet<RunState>> = {
  queued: new Set(['preparing', 'cancelling', 'terminal']),
  preparing: new Set(['exploring', 'running', 'cancelling', 'terminal']),
  exploring: new Set(['compiling', 'diagnosing', 'cancelling', 'terminal']),
  compiling: new Set(['stabilizing', 'cancelling', 'terminal']),
  stabilizing: new Set(['running', 'diagnosing', 'cancelling', 'terminal']),
  running: new Set(['diagnosing', 'cancelling', 'terminal']),
  diagnosing: new Set(['exploring', 'stabilizing', 'cancelling', 'terminal']),
  cancelling: new Set(['terminal']),
  terminal: new Set()
}

export function canTransitionRunState(from: RunState, to: RunState): boolean {
  return from === to || transitions[from].has(to)
}

export function assertRunStateTransition(from: RunState, to: RunState): void {
  if (!canTransitionRunState(from, to)) {
    throw new Error(`Invalid web-test run state transition: ${from} -> ${to}`)
  }
}

export interface FailureSignals {
  terminalStatus: RunStatus
  deterministicAttempts: number
  equivalentFailures: number
  passedAfterRetry?: boolean
  infrastructureFailure?: boolean
  diagnosis?: FailureClassification
}

export function classifyFailure(signals: FailureSignals): FailureClassification | undefined {
  if (signals.terminalStatus === 'passed') {
    return signals.passedAfterRetry ? 'flaky' : undefined
  }
  if (signals.infrastructureFailure || signals.terminalStatus === 'infrastructure-error') {
    return 'infrastructure-error'
  }
  if (signals.terminalStatus === 'needs-review') return 'needs-review'
  if (signals.diagnosis) return signals.diagnosis
  if (signals.deterministicAttempts < 2 || signals.equivalentFailures < 2) return 'unknown'
  return 'needs-review'
}

export type DiagnosedFailureOutcome =
  | {
      action: 'repair'
      status: 'needs-review'
      classification: 'test-drift'
    }
  | {
      action: 'finish'
      status: 'failed' | 'infrastructure-error' | 'needs-review'
      classification: FailureClassification
    }

/**
 * Maps a structured diagnosis to the only workflow branch it is allowed to take.
 * Keeping this policy shared prevents local and remote runners from interpreting
 * an ambiguous or infrastructure diagnosis as an ordinary product failure.
 */
export function diagnosedFailureOutcome(
  classification: FailureClassification
): DiagnosedFailureOutcome {
  switch (classification) {
    case 'test-drift':
      return { action: 'repair', status: 'needs-review', classification }
    case 'infrastructure-error':
      return { action: 'finish', status: 'infrastructure-error', classification }
    case 'needs-review':
    case 'unknown':
      return { action: 'finish', status: 'needs-review', classification }
    case 'product-failure':
    case 'scenario-drift':
    case 'flaky':
      return { action: 'finish', status: 'failed', classification }
  }
}

export function repairReviewOutcome(input: { candidateGenerated: boolean; stable: boolean }): {
  status: 'needs-review'
  classification: 'test-drift' | 'needs-review'
  summary: string
} {
  if (!input.candidateGenerated) {
    return {
      status: 'needs-review',
      classification: 'test-drift',
      summary: 'The committed plan is stale, but no stable replacement candidate was generated'
    }
  }
  if (!input.stable) {
    return {
      status: 'needs-review',
      classification: 'needs-review',
      summary: 'The generated replacement plan was not stable and needs developer review'
    }
  }
  return {
    status: 'needs-review',
    classification: 'test-drift',
    summary: 'A replacement plan passed clean replays and is ready for explicit review'
  }
}
